<template>
  <div class="contents contents--full">
    <div class="contents__wrap">
      <div class="sub-top">
        <h3>{{ orgName }}의 품질평가 결과</h3>
        <select v-model="selectedYear" class="year-dropdown">
          <option v-for="year in availableYears" :key="year" :value="year">
            {{ year }}
          </option>
        </select>
        <button @click="yearLoadData" class="excel-download-btn">조회</button>
        <button @click="downloadExcel" class="excel-download-btn">엑셀 다운로드</button>
        <div class="sub-top__navi">
          <span class="icon-home navi--home"></span>
          <span>{{ orgName }}의 품질평가 결과</span>
        </div>
      </div>

      <div class="summary-table-container">
        <table class="summary-table">
          <thead>
            <tr>
              <th rowspan="2" @click="resetFilter">기관 구분</th>
              <th rowspan="2" @click="resetFilter">품질평가 총점</th>
              <template v-for="(domain, index) in transformedDomains" :key="`header-${index}`">
                <th @click="filterByDomain(domain.domainCode)">
                  {{ domain.domainName.split('(')[0] }}
                  <br />({{ domain.domainName.match(/\((.*?)\)/)?.[1] || '' }})
                </th>
              </template>
            </tr>
          </thead>
    
          <tbody>
            <tr>
              <td >
                {{ orgName }}
              </td>
              <!-- 품질평가 총점 -->
              <td :class="getScoreClass(calculateFinalScore() || 0.00)">
                {{ calculateFinalScore() || 0.00 }}
              </td>
              <!-- 각 도메인 점수 -->
              <template v-for="(domain, index) in transformedDomains" :key="`score-${index}`">
                <td :class="getScoreClass(calculateDomainScore1(domain.domainCode))">
                  {{ calculateDomainScore1(domain.domainCode) || 0.00 }}
                </td>
              </template>
            </tr>
          </tbody>

          <tbody>
            <tr>
              <td>타기관</td>
              <!-- 품질평가 총점 -->
              
              <td :class="getScoreClass(anotherFinalScore)">
                {{ anotherFinalScore || 0.00 }}
              </td>
             
              <!-- 각 도메인 점수 -->
              <template v-for="(domain, index) in transformedDomains" :key="`domain-score-${index}`">
                <td :class="getScoreClass(anotherDomainScore1(domain.domainCode))">
                  {{ anotherDomainScore1(domain.domainCode) || 0.00 }}
                </td>
              </template>
            </tr>
          </tbody>

          <tbody>
            <tr>
              <td>차이</td>
              <!-- 품질평가 총점 차이 -->
              <td>
                {{ totalScoreDifference || 0.00 }}
              </td>
              <!-- 각 도메인 점수 차이 -->
              <template v-for="(domain, index) in transformedDomains" :key="`diff-score-${index}`">
                <td>
                  {{ domainScoreDifferences[domain.domainCode] || 0.00 }}
                </td>
              </template>
            </tr>
          </tbody>


        </table>

        <br />

        <!-- 통계 테이블 -->
        <div class="stats-table-container">
          <table class="stats-table">

            <thead>
              <tr>
                <th>Domain</th>
                <th>Principle</th>
                <th>Standard</th>
                <th>세부평가코드</th>
                <th>세부평가사항</th>
                <th>세부평가점수</th>
                <th>Standard 점수</th>
                <th>타기관 Standard 점수</th>
                <th>Principle 점수</th>
                <th>타기관 Principle 점수</th>
                <th>Domain 점수</th>
                <th>타기관 Domain 점수</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="(domain, domainIndex) in sortedFilteredDomains" :key="domainIndex">
                <template v-for="(principle, principleIndex) in domain.principles" :key="principleIndex">
                  <template v-for="(standard, standardIndex) in principle.standards" :key="standardIndex">
                    <template v-for="(evaluation, evalIndex) in standard.evaluations" :key="evalIndex">
                      <tr>
                        <!-- Domain -->
                        <td v-if="principleIndex === 0 && standardIndex === 0 && evalIndex === 0" 
                            :rowspan="getDomainRowspan(domain)" 
                            v-html="formatName(domain.domainName)">
                        </td>
                        <!-- Principle -->
                        <td v-if="standardIndex === 0 && evalIndex === 0" 
                            :rowspan="getPrincipleRowspan(principle)" 
                            v-html="formatName(principle.principleName)">
                        </td>
                        <!-- Standard -->
                        <td v-if="evalIndex === 0" 
                            :rowspan="standard.evaluations.length" 
                            v-html="formatName(standard.standardName)">
                        </td>
                        <!-- Evaluation -->
                        <td>{{ evaluation.evaluationCode }}</td>
                        <td>{{ evaluation.evaluationName }}</td>
                        <td>{{ evaluationScores[evaluation.evaluationCode] || 0.00 }}</td>
                        <!-- Standard 가중치 -->
                        <td v-if="evalIndex === 0" :rowspan="standard.evaluations.length">
                          {{ calculateStandardScore1(standard.standardCode || 'A') }}
                        </td>
                        <td v-if="evalIndex === 0" :rowspan="standard.evaluations.length" class="avg-score">
                          {{ anotherStandardScore1(standard.standardCode || 'A') }}
                        </td>
                        <!-- Principle 가중치 -->
                        <td v-if="standardIndex === 0 && evalIndex === 0" :rowspan="getPrincipleRowspan(principle)">
                          {{ calculatePrincipleScore1(principle.principleCode || 'A') }}
                        </td>
                        <td v-if="standardIndex === 0 && evalIndex === 0" :rowspan="getPrincipleRowspan(principle)" class="avg-score">
                          {{ anotherPrincipleScore1(principle.principleCode || 'A') }}
                        </td>
                        <!-- Domain 가중치 -->
                        <td v-if="principleIndex === 0 && standardIndex === 0 && evalIndex === 0" :rowspan="getDomainRowspan(domain)">
                          {{ calculateDomainScore1(domain.domainCode) || 0.00 }}
                        </td>
                        <td v-if="principleIndex === 0 && standardIndex === 0 && evalIndex === 0" :rowspan="getDomainRowspan(domain)" class="avg-score">
                          {{ anotherDomainScore1(domain.domainCode) || 0.00 }}
                        </td>
                      </tr>
                    </template>
                  </template>
                </template>
              </template>
            </tbody>



          </table>
        </div>
      </div>
    </div>
  </div>

</template>


<script setup>
import { reactive, ref, computed, onMounted,onUnmounted  } from "vue";
import { useStore } from "vuex";
import { useRouter,useRoute } from 'vue-router'
import * as XLSX from "xlsx";
const store = useStore();
const route = useRoute()
// 필터링 상태
const selectedDomainCode = ref(null);

// 도메인 데이터 가져오기
const domainList = computed(() => store.state.grid.domainData);// 도메인 데이터 가져오기
const orgEvlList = computed(() => store.state.grid.evlOrgData2);// 평가데이터 가져오기기
const anotherEvlList = computed(() => store.state.grid.evlOrgData);// 타기관 평가데이터 가져오기기

const checkUserId = computed(() => store.state.temp.userInfo);

// 연도 콤보박스 선택된 값
const selectedYear = ref('2025');

// 연도 목록 (현재 연도부터 과거 5년까지)
const availableYears = ref(
  Array.from({ length: 3 }, (_, i) => new Date().getFullYear() - i)
);

//해당 기관 점수 매핑 관련 코드들 
// 평가 점수 매핑
const evaluationScores = computed(() => {
  const scores = {};
  orgEvlList.value.forEach((item) => {
    if (item.evaluationCode && item.evaluationScore) {
      scores[item.evaluationCode] = item.evaluationScore;
    }
  });
  return scores;
});

// 현황 매핑
const evaluationCurrents = computed(() => {
  const scores = {};
  orgEvlList.value.forEach((item) => {
    if (item.evaluationCode && item.evaluationCurrent) {
      scores[item.evaluationCode] = item.evaluationCurrent;
    }
  });
  return scores;
});

// 개선사항 매핑
const evaluationImps = computed(() => {
  const scores = {};
  orgEvlList.value.forEach((item) => {
    if (item.evaluationCode && item.evaluationImp) {
      scores[item.evaluationCode] = item.evaluationImp;
    }
  });
  return scores;
});

const eqcComments = computed(() => {
  const scores = {};
  orgEvlList.value.forEach((item) => {
    if (item.evaluationCode && item.eqcComment) {
      scores[item.evaluationCode] = item.eqcComment;
    }
  });
  return scores;
});

// 세부평가 가중치
const evaluationWeightScores = computed(() => {
  const scores = {};
  orgEvlList.value.forEach((item) => {
    if (item.evaluationCode && item.evaluationWeight) {
      scores[item.evaluationCode] = item.evaluationWeight;
    }
  });
  return scores;
});

// 세부평가 가중치 적용점수
const detailEvaluationWeight = computed(() => {
  const scores = {};
  orgEvlList.value.forEach((item) => {
    if (item.evaluationCode && item.detailEvaluationWeight) {
      scores[item.evaluationCode] = item.detailEvaluationWeight;
    }
  });
  return scores;
});


// Standard 1 점수 매핑
const standardScores = computed(() => {
  const scores = {};
  orgEvlList.value.forEach((item) => {
    if (item.standardCode && item.standardScore1) { // 🔹 Standard 1 점수 매핑
      scores[item.standardCode] = item.standardScore1;
    }
  });
  return scores;
});

// Standard 1 점수를 그대로 반환하는 함수
const calculateStandardScore1 = (standardCode) => {
  return standardScores.value[standardCode] ? standardScores.value[standardCode].toFixed(2) : 0.00;
};

// Principle 1 점수 매핑
const principleScores = computed(() => {
  const scores = {};
  orgEvlList.value.forEach((item) => {
    if (item.principleCode && item.principleScore1 !== undefined) { // 🔹 null 방지
      scores[item.principleCode] = item.principleScore1;
    }
  });
  return scores;
});

// Principle 1 점수 조회 함수
const calculatePrincipleScore1 = (principleCode) => {
  if (!principleCode || !principleScores.value[principleCode]) {
    return 0.00;
  }
  return principleScores.value[principleCode].toFixed(2);
};

// ✅ Domain 점수 매핑
const domainScores = computed(() => {
  const scores = {};
  orgEvlList.value.forEach((item) => {
    if (item.domainCode && item.domainScore1 !== undefined) {
      scores[item.domainCode] = item.domainScore1;
    }
  });
 
  return scores;
});

// ✅ Domain 점수 매핑
const avgDomainScores = computed(() => {
  const scores = {};
  orgEvlList.value.forEach((item) => {
    if (item.domainCode && item.avgDomainScore !== undefined) {
      scores[item.domainCode] = item.avgDomainScore;
    }
  });
 
  return scores;
});

const calculateDomainScore1 = (domainCode) => {
  return domainScores.value[domainCode]?.toFixed(2) || '0.00';
};

// ✅ Final Score (최종 점수) 매핑
const finalScore = computed(() => {
  let score = null;
  
  // API 데이터에서 finalScore 값을 찾음
  if (orgEvlList.value.length > 0) {
    score = orgEvlList.value[0]?.finalScore; // 첫 번째 항목에서 최종 점수 가져오기
  }

  //console.log("🔹 finalScore 데이터 확인:", score); // 🔍 디버깅 로그
  return score;
});

// ✅ Final Score 조회 함수
const calculateFinalScore = () => {
  const score = Number(finalScore.value);
  return isNaN(score) ? 0.00 : score.toFixed(2); // 🔥 NaN 방지
};


/**
 * 
 * 
 * 
 * 타기관 점수 관련련
 * 
 * 
 * 
 * 
 */
// 타기관 스탠다드 평균 점수 매핑
const anotherStandardScores = computed(() => {
  const scores = {};
  anotherEvlList.value.forEach((item) => {
    if (item.standardCode && item.standardScore1) { // 🔹 Standard 1 점수 매핑
      scores[item.standardCode] = item.standardScore1;
    }
  });
  return scores;
});



// 타기관 스탠다드 평균
const anotherStandardScore1 = (standardCode) => {
  return anotherStandardScores.value[standardCode] ? anotherStandardScores.value[standardCode].toFixed(2) : 0.00;
};


// 타기관 프린서펄1 점수매핑
const anotherPrincipleScores = computed(() => {
  const scores = {};
  anotherEvlList.value.forEach((item) => {
    if (item.principleCode && item.principleScore1 !== undefined) { // 🔹 null 방지
      scores[item.principleCode] = item.principleScore1;
    }
  });
  return scores;
});

// Principle 1 점수 조회 함수
const anotherPrincipleScore1 = (principleCode) => {
  if (!principleCode || !anotherPrincipleScores.value[principleCode]) {
    return 0.00;
  }
  return anotherPrincipleScores.value[principleCode].toFixed(2);
};

//타기관 도메인점수
const anotherDomainScores = computed(() => {
  const scores = {};
  anotherEvlList.value.forEach((item) => {
    if (item.domainCode && item.domainScore1 !== undefined) {
      scores[item.domainCode] = item.domainScore1;
    }
  });
 
  return scores;
});

const anotherDomainScore1 = (domainCode) => {
  return anotherDomainScores.value[domainCode]?.toFixed(2) || '0.00';
};

// totalAvgDomainScore
const anotherFinalScore = computed(() => {
  let score = null;
  
  // API 데이터에서 finalScore 값을 찾음
  if (anotherEvlList.value.length > 0) {
    score = anotherEvlList.value[0]?.finalScore; // 첫 번째 항목에서 최종 점수 가져오기
  }

  //console.log("🔹 finalScore 데이터 확인:", score); // 🔍 디버깅 로그
  return Number(score).toFixed(2);
});

// 필터링된 도메인 데이터
const filteredDomains = computed(() => {
  if (!selectedDomainCode.value) {
    return transformedDomains.value; // 필터가 없을 때 전체 데이터 반환
  }
  return transformedDomains.value.filter(
    (domain) => domain.domainCode === selectedDomainCode.value
  );
});

const domainScoreDifferences = computed(() => {
  const differences = {};
  transformedDomains.value.forEach(domain => {
    const orgScore = calculateDomainScore1(domain.domainCode) || 0; // 해당 기관의 도메인 점수
    const avgScore = anotherDomainScore1(domain.domainCode) || 0; // 타기관 평균 도메인 점수
    differences[domain.domainCode] = (orgScore - avgScore).toFixed(2); // 차이 계산 (소수점 2자리)
  });
  return differences;
});

const totalScoreDifference = computed(() => {
  const orgTotal = calculateFinalScore() || 0;
  const avgTotal = anotherFinalScore.value || 0;
  return (orgTotal - avgTotal).toFixed(2);
});


// 통계 테이블 정렬된 데이터
const sortedFilteredDomains = computed(() => {
  return [...filteredDomains.value].sort((a, b) => {
    // Domain 코드가 "A"로 시작하는 경우를 우선 처리
    const aIsA = a.domainCode.startsWith('A');
    const bIsA = b.domainCode.startsWith('A');
    
    if (aIsA && !bIsA) return -1; // 'A'로 시작하는 도메인은 먼저
    if (!aIsA && bIsA) return 1; // 'A'로 시작하지 않는 도메인은 나중
    
    // 숫자 기반 정렬
    return a.domainCode.localeCompare(b.domainCode, undefined, { numeric: true });
  });
});


// 도메인 필터링 함수
const filterByDomain = (domainCode) => {
  selectedDomainCode.value = domainCode;
};

// 필터 초기화 함수
const resetFilter = () => {
  selectedDomainCode.value = null;
};


// 트리 구조 변환 함수
const transformToTreeStructure = (data) => {
  const groupedData = {};

  data.forEach((item) => {
    if (!groupedData[item.domainCode]) {
      groupedData[item.domainCode] = {
        domainCode: item.domainCode,
        domainName: item.domainName,
        principles: {},
      };
    }
    const domain = groupedData[item.domainCode];

    if (!domain.principles[item.principleCode]) {
      domain.principles[item.principleCode] = {
        principleCode: item.principleCode,
        principleName: item.principleName,
        standards: {},
      };
    }
    const principle = domain.principles[item.principleCode];

    if (!principle.standards[item.standardCode]) {
      principle.standards[item.standardCode] = {
        standardCode: item.standardCode,
        standardName: item.standardName,
        evaluations: [],
      };
    }
    const standard = principle.standards[item.standardCode];

    standard.evaluations.push({
      evaluationCode: item.evaluationCode,
      evaluationName: item.evaluationName,
    });
  });

  return Object.values(groupedData).map((domain) => ({
    ...domain,
    principles: Object.values(domain.principles).map((principle) => ({
      ...principle,
      standards: Object.values(principle.standards),
    })),
  }));
};

// 트리 데이터 생성
const transformedDomains = computed(() => transformToTreeStructure(domainList.value));

// rowspan 계산
const getDomainRowspan = (domain) => {
  return domain.principles.reduce((total, principle) => {
    return total + principle.standards.reduce((count, standard) => count + standard.evaluations.length, 0);
  }, 0);
};

const sortedDomains = computed(() => {
  return [...transformedDomains.value].sort((a, b) => {
    const aStartsWithA = a.domainCode.match(/^[A]/); // A로 시작하는지 확인
    const bStartsWithA = b.domainCode.match(/^[A]/); // A로 시작하는지 확인

    if (aStartsWithA && !bStartsWithA) return -1; // A가 먼저
    if (!aStartsWithA && bStartsWithA) return 1; // A가 나중

    // A가 아니면 숫자 기반으로 정렬
    return a.domainCode.localeCompare(b.domainCode, undefined, { numeric: true });
  });
});


const downloadExcel = () => {
  const workbook = XLSX.utils.book_new();

  /** 📊 1. 요약 테이블 시트 생성 */
  const summaryWorksheetData = [];
  const summaryHeaders = ["품질평가 총점"];
  transformedDomains.value.forEach(domain => {
    summaryHeaders.push(domain.domainName.split('(')[0]);
  });
  summaryWorksheetData.push(summaryHeaders);

  const summaryRow = [calculateFinalScore() || 0.00];
  transformedDomains.value.forEach(domain => {
    summaryRow.push(calculateDomainScore1(domain.domainCode) || 0.00);
  });
  summaryWorksheetData.push(summaryRow);

  const summaryWorksheet = XLSX.utils.aoa_to_sheet(summaryWorksheetData);
  XLSX.utils.book_append_sheet(workbook, summaryWorksheet, "요약 테이블");


  // selector(a)로 코드 문자열을 뽑아와 정렬.
  // 규칙: 알파벳으로 시작(A.*) 우선 → 같은 타입끼리는 숫자 인식 정렬
  const sortByCode = (selector) => (a, b) => {
    const ac = (selector(a) || '').trim();
    const bc = (selector(b) || '').trim();

    const aAlpha = /^[A-Za-z]/.test(ac);
    const bAlpha = /^[A-Za-z]/.test(bc);
    if (aAlpha !== bAlpha) return aAlpha ? -1 : 1;

    // 숫자 인식 정렬: 'A.2' < 'A.10', '1.2.9' < '1.2.10'
    return ac.localeCompare(bc, 'ko', { numeric: true });
  };


  /** 📊 2. 품질평가 결과 통계 테이블 시트 생성 */
  const statsWorksheetData = [];
  const statsHeaders = [
    "Domain",
    "Principle",
    "Standard",
    "세부평가코드",
    "세부평가사항",
    "현황",
    "개선사항",
    "개선권고사항",
    "세부평가점수",
    "Standard 점수",
    "Principle 점수",
    "Domain 점수"
  ];
  statsWorksheetData.push(statsHeaders);

  let currentRow = 1;

  const sortedDomainsForStats = transformedDomains.value
    .slice()
    .sort(sortByCode(d => d.domainCode || d.domainName)); // domainCode가 있으면 그걸 우선

  sortedDomainsForStats.forEach(domain => {
    let isFirstDomainRow = true;

    const sortedPrinciples = (domain.principles || [])
      .slice()
      .sort(sortByCode(p => p.principleCode || p.principleName));

    sortedPrinciples.forEach(principle => {
      let isFirstPrincipleRow = true;

      const sortedStandards = (principle.standards || [])
        .slice()
        .sort(sortByCode(s => s.standardCode || s.standardName));

      sortedStandards.forEach(standard => {
        let isFirstStandardRow = true;

        const sortedEvaluations = (standard.evaluations || [])
          .slice()
          .sort(sortByCode(e => e.evaluationCode));

        // (선택) 디버깅
        // console.log(
        //   '도메인/프린서펄/스탠다드/평가 정렬:',
        //   (domain.domainCode || domain.domainName),
        //   (principle.principleCode || principle.principleName),
        //   (standard.standardCode || standard.standardName),
        //   sortedEvaluations.map(e => e.evaluationCode)
        // );

        sortedEvaluations.forEach(evaluation => {
          const row = [
            isFirstDomainRow ? domain.domainName : "",
            isFirstPrincipleRow ? principle.principleName : "",
            isFirstStandardRow ? standard.standardName : "",
            evaluation.evaluationCode,
            evaluation.evaluationName,
            evaluationCurrents.value[evaluation.evaluationCode] || '데이터 없음',
            evaluationImps.value[evaluation.evaluationCode] || '데이터 없음',
            eqcComments.value[evaluation.evaluationCode] || '데이터 없음',
            evaluationScores.value[evaluation.evaluationCode] || 0.00,
            isFirstStandardRow ? (calculateStandardScore1(standard.standardCode || "A") || 0.00) : "",
            isFirstPrincipleRow ? (calculatePrincipleScore1(principle.principleCode || "A") || 0.00) : "",
            isFirstDomainRow ? (calculateDomainScore1(domain.domainCode) || 0.00) : ""
          ];
          statsWorksheetData.push(row);
          currentRow++;

          isFirstDomainRow = false;
          isFirstPrincipleRow = false;
          isFirstStandardRow = false;
        });
      });
    });
  });


  const statsWorksheet = XLSX.utils.aoa_to_sheet(statsWorksheetData);
  XLSX.utils.book_append_sheet(workbook, statsWorksheet, "품질평가 결과");

  /** 📊 3. 품질평가 상세 결과 시트 추가 */
  const qualityAssessmentData = [];
  const qualityHeaders = [
    "Domain",
    "Principle",
    "Standard",
    "세부평가코드",
    "세부평가사항",
    "세부평가점수",
    "세부평가 가중치",
    "세부평가 가중치 적용점수",
    "Standard 점수",
    "Standard 가중치",
    "Standard 가중치 적용점수",
    "Principle 점수",
    "Principle 가중치",
    "Principle 가중치 적용점수",
    "Domain 점수",
    "Domain 가중치",
    "Domain 가중치 적용점수",
    "품질평가 총점"
  ];
  qualityAssessmentData.push(qualityHeaders);

  let qualityCurrentRow = 1;
  let isFirstFinalScoreRow = true; // finalScore는 첫 번째 행에만 입력

  const standardScoresMap = {};
  const standardWeightMap = {};
  const standardWeightScoreMap = {};
  const principleScoresMap = {};
  const principleWeightMap = {};
  const principleWeightScoresMap = {};
  const domainScoresMap = {};
  const domainWeightMap = {};
  const domainWeightScoresMap = {};
  let finalScore = 0.00;

  orgEvlList.value.forEach((item) => {
    //스탠다드점수 1 매핑핑
    if (item.standardCode && item.standardScore1 !== undefined) {
      standardScoresMap[item.standardCode] = item.standardScore1;
    }
    //스탠다드 가중치
    if (item.standardCode && item.standardWeight !== undefined) {
      standardWeightMap[item.standardCode] = item.standardWeight;
    }
     //스탠다드 가중치 적용점수
     if (item.evaluationCode && item.standardWeightedScore !== undefined) {
      standardWeightScoreMap[item.evaluationCode] = item.standardWeightedScore;
    }
    //프린서펄 점수1 매핑
    if (item.principleCode && item.principleScore1 !== undefined) {
      principleScoresMap[item.principleCode] = item.principleScore1;
    }
    //프린서펄 가중치
    if (item.principleCode && item.principleWeight !== undefined) {
      principleWeightMap[item.principleCode] = item.principleWeight;
    }
    //프린서펄 점수1 매핑
    if (item.principleCode && item.principleScore2 !== undefined) {
      principleWeightScoresMap[item.principleCode] = item.principleScore2;
    }
    //도메인 점수1매핑
    if (item.domainCode && item.domainScore1 !== undefined) {
      domainScoresMap[item.domainCode] = item.domainScore1;
    }
     //도메인 가중치
     if (item.domainCode && item.domainWeight !== undefined) {
      domainWeightMap[item.domainCode] = item.domainWeight;
    }
     //도메인 도메인 가중치 적용점수
     if (item.domainCode && item.domainScore2 !== undefined) {
      domainWeightScoresMap[item.domainCode] = item.domainScore2;
    }
    if (item.finalScore !== undefined) {
      finalScore = item.finalScore.toFixed(2);
    }
  });

  sortedDomains.value.forEach(domain => {
    let isFirstDomainRow = true;

    domain.principles.forEach(principle => {
      let isFirstPrincipleRow = true;

      principle.standards.forEach(standard => {
        let isFirstStandardRow = true;

        standard.evaluations.forEach((evaluation, index) => {
          const row = [
            isFirstDomainRow ? domain.domainName : "", // "Domain",
            isFirstPrincipleRow ? principle.principleName : "", // "Principle",
            isFirstStandardRow ? standard.standardName : "", // "Standard",
            evaluation.evaluationCode, // 세부평가 코드 
            evaluation.evaluationName, // 세부평가 이름름
            evaluationScores.value[evaluation.evaluationCode] || 0.00, // 세부평가점수
            evaluationWeightScores.value[evaluation.evaluationCode] || 0.00, // 세부평가가중치
            detailEvaluationWeight.value[evaluation.evaluationCode] || 0.00,//세부평가 가중치 적용점수 
            isFirstStandardRow ? (standardScoresMap[standard.standardCode || "A"]?.toFixed(2) || 0.00) : "", // "Standard 점수",
            isFirstStandardRow ? (standardWeightMap[standard.standardCode || "A"]?.toFixed(2) || "가중치없음") : "", //  "Standard 가중치",
            standardWeightScoreMap[evaluation.evaluationCode || "A"]?.toFixed(2) || "가중치없음",//standardWeightScoreMap
            isFirstPrincipleRow ? (principleScoresMap[principle.principleCode || "A"]?.toFixed(2) || 0.00) : "", // "Principle 점수",
            isFirstPrincipleRow ? (principleWeightMap[principle.principleCode || "A"]?.toFixed(2) || 0.00) : "", // "Principle 가중치",
            isFirstPrincipleRow ? (principleWeightScoresMap[principle.principleCode || "A"]?.toFixed(2) || 0.00) : "", // "Principle 가중치 적용점수",
            isFirstDomainRow ? (domainScoresMap[domain.domainCode]?.toFixed(2) || 0.00) : "", // 도메인 점수
            isFirstDomainRow ? (domainWeightMap[domain.domainCode]?.toFixed(2) || 0.00) : "", // 도메인 가중치
            isFirstDomainRow ? (domainWeightScoresMap[domain.domainCode]?.toFixed(2) || 0.00) : "", // 도메인 가중치 적용점수
            isFirstFinalScoreRow ? finalScore : "" // 품질평가 총점은 첫 행에만 입력
          ];

          qualityAssessmentData.push(row);
          qualityCurrentRow++;

          isFirstDomainRow = false;
          isFirstPrincipleRow = false;
          isFirstStandardRow = false;
          isFirstFinalScoreRow = false; // 첫 번째 행 이후엔 빈 값 처리
        });
      });
    });
  });

  const qualityAssessmentWorksheet = XLSX.utils.aoa_to_sheet(qualityAssessmentData);
  XLSX.utils.book_append_sheet(workbook, qualityAssessmentWorksheet, "품질평가 상세 결과");

  // /** 📊 4. 모든 기업 전체 통계 요약 시트 생성 */
  // const overallSummaryWorksheetData = [];
  // const overallSummaryHeaders = [
  //   "전체 평균",
  //   "Domain 평균",
  //   "Principle 평균",
  //   "Standard 평균"
  // ];
  // overallSummaryWorksheetData.push(overallSummaryHeaders);

  // giasAllAvgList.value.forEach(vo => {
  //   const row = [
  //     vo?.allAvg || 0.00,
  //     vo?.domainAvg || 0.00,
  //     vo?.prinAvg || 0.00,
  //     vo?.stanAvg || 0.00
  //   ];
  //   overallSummaryWorksheetData.push(row);
  // });

  // const overallSummaryWorksheet = XLSX.utils.aoa_to_sheet(overallSummaryWorksheetData);
  // XLSX.utils.book_append_sheet(workbook, overallSummaryWorksheet, "모든 기업 통계 요약");

  // /** 📊 5. 기업별 통계 요약 시트 생성 */
  // const companySummaryWorksheetData = [];
  // const companySummaryHeaders = [
  //   "기관 이름",
  //   "전체 평균",
  //   "Domain 평균",
  //   "Principle 평균",
  //   "Standard 평균"
  // ];
  // companySummaryWorksheetData.push(companySummaryHeaders);

  // giasAvgList.value.forEach(vo => {
  //   const row = [
  //     vo?.organizationName || 0.00,
  //     vo?.allAvg || 0.00,
  //     vo?.domainAvg || 0.00,
  //     vo?.prinAvg || 0.00,
  //     vo?.stanAvg || 0.00
  //   ];
  //   companySummaryWorksheetData.push(row);
  // });

  //const companySummaryWorksheet = XLSX.utils.aoa_to_sheet(companySummaryWorksheetData);
  //XLSX.utils.book_append_sheet(workbook, companySummaryWorksheet, "기업별 통계 요약");

  /** 📊 6. 엑셀 파일 생성 및 다운로드 */
  XLSX.writeFile(workbook, `${orgName.value}_통계_전체_테이블.xlsx`);
};

const getPrincipleRowspan = (principle) => {
  return principle.standards.reduce((count, standard) => count + standard.evaluations.length, 0);
};

// 텍스트에 <br> 삽입하는 함수
const formatName = (name) => {
  if (!name) return "";
  return name.replace(/\(/g, "<br>("); // '(' 이전에 <br> 추가
};

// 요약 그리드 점수별로 배경색 바꾸기
const getScoreClass = (score) => {
  if (!score || score === "평가중") return "score-pending";
  if (score >= 80) return "score-excellent";
  if (score >= 60) return "score-satisfactory";
  if (score >= 40) return "score-needs-improvement";
  if (score >= 20) return "score-weak";
  return "score-very-weak";
};

// 모든 평가 항목 조회
const getGridData = async () => {
  let param = {
    memberCode: checkUserId.value.memberCode,
  };
  await store.dispatch("grid/fetchAllDomain", param);
};

// 저장한 평가 데이터 조회
const yearLoadData = async () => {
  let param = {
    organizationCode: orgCode.value,
    orgGiasYear : selectedYear.value
  };
  //await store.dispatch("grid/fetchAdminEvlOrg2", param); fetchSelectGiasWeight
  await store.dispatch("grid/fetchSelectGiasWeight", param);

  //await store.dispatch("grid/fetchAdminEvlOrg2", param); fetchSelectGiasWeight
  await store.dispatch("grid/fetchSelectAnotherOrgWeight", param);

};

const getEvlData = async () => {
  let param = {
    organizationCode: orgCode.value,
    orgGiasYear : '2025'
  };
  //await store.dispatch("grid/fetchAdminEvlOrg2", param); fetchSelectGiasWeight
  await store.dispatch("grid/fetchSelectGiasWeight", param);
};
// 저장한 평가 데이터 조회
const getAnotherEvlData = async () => {
  let param = {
    organizationCode: orgCode.value,
    orgGiasYear : '2025'
  };
  await store.dispatch("grid/fetchSelectAnotherOrgWeight", param);
};

let orgName = ref('')
let orgCode = ref('')

onMounted(async () => {
  orgName.value = route.query?.organizationName || ''; 
  orgCode.value = route.query?.organizationCode || ''; 

  //console.log("기관이름" + orgName.value )
  await getGridData(); //메인 그리드 평가항목리스트
  await getEvlData(); // 평가항목에 저장된 점수를 조회
  await getAnotherEvlData();
  // await getEvl3Year(); // 3년치 평가 점수 평균 데이터
  // await getGiasAvg();  // 모든 기업의 전체 통계 요약
  // await getGiasAllAvg(); // 기업별 통계 요약
});

// 컴포넌트 언마운트 시 호출
onUnmounted(() => {
  // Store의 clearEvlOrgData를 호출
  store.commit("grid/clearEvlOrgData2");
})
</script>

<style scoped>
.stats-table {
  width: 100%;
  border-collapse: collapse;
  text-align: left;
}

.stats-table th{
  border: 1px solid #ddd;
  padding: 8px;
  text-align: center; /* 셀 텍스트 수평 중앙 정렬 */
  vertical-align: middle; /* 셀 텍스트 수직 중앙 정렬 */
  line-height: 1.6; /* 줄 간격 조정 */
  font-size: 12px;
}
.stats-table td {
  border: 1px solid #ddd;
  padding: 8px;
  text-align: center; /* 셀 텍스트 수평 중앙 정렬 */
  vertical-align: middle; /* 셀 텍스트 수직 중앙 정렬 */
  line-height: 1.4;
  font-size: 10px;
}

.stats-table th {
  background-color: #f4f4f4;
  font-weight: bold;
  text-align: center;
  vertical-align: middle;
}

.stats-table td {
  vertical-align: top;
}

/* 요약 테이블 스타일 */

.summary-table-container {
  background-color: white; /* 배경색 유지 */
  box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.1); /* 그림자 효과 */
  border-bottom: 2px solid #ddd; /* 하단 테두리 */
  overflow: hidden; /* 전체 페이지 스크롤 제거 */
  display: flex;
  flex-direction: column;
}


.summary-table {
  position: sticky;
  top: 0; /* 화면 상단 고정 */
  z-index: 10; /* 통계 테이블보다 위에 배치 */
  background-color: white; /* 배경색 추가 */
  box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.1); /* 그림자 효과 */
  border-bottom: 2px solid #ddd; /* 구분선 */
}

.summary-table th,
.summary-table td {
  border: 1px solid #000;
  padding: 10px;
}

/* 📌 고정된 영역이 상단에 가려지지 않도록 공간 추가 */
.container {
  padding-top: 60px; /* 요약 테이블 높이만큼 패딩 추가 */
}

.summary-table .highlight {
  background-color: #00a651;
  color: #fff;
  font-weight: bold;
}


/* 점수에 따른 배경색 */
.score-excellent {
  background-color: #00a651; /* 매우 충족 - 초록 */
  color: white;
}

.score-satisfactory {
  background-color: #92d050; /* 충족 - 연두 */
  color: black;
}

.score-needs-improvement {
  background-color: #ffff00; /* 개선여지 - 노랑 */
  color: black;
}

.score-weak {
  background-color: #ffc000; /* 취약 - 주황 */
  color: black;
}

.score-very-weak {
  background-color: #ff0000; /* 매우 취약 - 빨강 */
  color: white;
}

.score-pending {
  background-color: #eaeaea; /* 0.00 - 회색 */
  color: black;
}

/**  통계 데이터의 헤더가 따라옴*/
.stats-table thead tr {
  z-index: 5; /* 요약 테이블보다 아래에 위치 */
  background-color: #f4f4f4; /* 배경색 (겹치는 부분 가독성) */
  border-bottom: 2px solid #ddd; /* 헤더 하단 선 */
  padding: 8px;
  font-weight: bold;
  text-align: center;
  vertical-align: middle;
}

.excel-download-container {
  position: fixed; /* 고정 위치 설정 */
  top: 20px; /* 화면 상단에서 20px */
  right: 20px; /* 화면 오른쪽에서 20px */
  z-index: 1000; /* 다른 요소 위에 표시되도록 설정 */
}

.excel-download-btn {
  background-color: #007bff; /* 파란색 버튼 */
  color: white; /* 텍스트 색상 */
  border: none;
  border-radius: 10px;
  padding: 7px 10px;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: background-color 0.2s;
  margin-left: 5px;
  margin-top : -2px;
}

.excel-download-btn:hover {
  background-color: #0056b3; /* 호버 시 색상 */
}

.summary-container {
  max-width: 100%;
  overflow-x: auto; /* 가로 스크롤 가능하도록 설정 */
  margin-bottom: 20px;
  border: 1px solid #ddd;
  border-radius: 5px;
  background-color: #f9fafc;
  padding: 15px;
}

.summary-container h4 {
  margin-bottom: 10px;
  text-align: center;
  font-size: 18px;
  font-weight: bold;
}

.overall-summary-table {
  width: 100%;
  border-collapse: collapse;
  text-align: center;
}

.overall-summary-table th,
.overall-summary-table td {
  border: 1px solid #ddd;
  padding: 10px;
  font-size: 12px;
}

.overall-summary-table th {
  background-color: #f4f4f4;
  font-weight: bold;
}

.overall-summary-table td {
  font-size: 12px;
  font-weight: bold;
}
/* 평균 컬럼을 검게 */
.avg-score {
  background-color: #f4f4f4; /* 검은색 배경 */
  font-weight: bold; /* 글씨 굵게 */
}

/* 🔵 통계 테이블 컨테이너 (여기서만 스크롤 발생) */
.stats-table-container {
  max-height: 600px; /* 📌 통계 테이블의 최대 높이 지정 */
  overflow-y: auto; /* 📌 세로 스크롤만 활성화 */
  border: 1px solid #ddd;
}

.year-dropdown {
  margin-left: 10px; /* 간격 추가 */
  padding: 2px 2px; /* 내부 패딩 */
  border: 1px solid #ccc; /* 테두리 스타일 */
  border-radius: 4px; /* 둥근 모서리 */
  font-size: 14px; /* 글꼴 크기 */
  cursor: pointer; /* 포인터 커서 */
  height: 25px;
  width: 50px;
  text-align: center;
}

.year-dropdown:focus {
  border-color: #0078d7; /* 포커스 시 테두리 색상 변경 */
  outline: none;
}
</style>



